/* BEGIN_COPYRIGHT_HEADER

Copyright Vspry International Limited (c) 2020
All rights reserved.

END_COPYRIGHT_HEADER */

import { createContext, ReactNode, useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useBrowserStorage, useFunctionState } from 'vspry-hooks'

import { Account, Contact, getUserContact, Holding, IDCard, recordUserLogin } from 'api/common'

import auth from 'services/auth'
import { AuthIdentity } from 'services/auth/interface'
import theme from 'styles/theme'

type AuthProviderState = {
    identity: AuthIdentity | null
    user: Contact | null
    account: Account | null
    products: (Holding | IDCard)[]
    currentProduct: Holding | IDCard | null
    tsAndCs: boolean
    fetching: boolean
    error: string | null
}

const init: AuthProviderState = {
    identity: null,
    user: null,
    account: null,
    products: [],
    currentProduct: null,
    tsAndCs: false,
    fetching: true,
    error: null,
}

const notifyIOSWebkitOfLogin = () => {
    if (window.webkit?.messageHandlers?.['logged-in']) window.webkit.messageHandlers['logged-in'].postMessage('logged-in')
}

const useAuthProvider = () => {
    const [state, setState] = useFunctionState(init)
    const { identity, user, account, products, currentProduct, tsAndCs, fetching, error } = state
    const [recordedLogin, setRecordedLogin] = useState(false)
    const [accID, setAccID] = useBrowserStorage('accountID', '')
    const [, setUsedPasskey] = useBrowserStorage('auth-used-passkey', false)

    useEffect(() => {
        if (currentProduct) {
            theme.setKeys(['buttonBG', 'primaryOBJ', 'secondaryOBJ', 'accent', 'textLink', 'highlight'], currentProduct.issuerPrimaryColor)
            theme.setKeys(['buttonText'], currentProduct.issuerTextColor)
        }
    }, [currentProduct])

    const setCurrentProduct = (p: Holding | IDCard | null) => {
        setState({ currentProduct: p })
    }

    const setCurrentAccount = (a: Account | null) => {
        setState({ account: a, currentProduct: a?.holdings[0] ?? null, products: a?.holdings ?? [] })
        if (a) setAccID(a.id)
    }

    const findAccount = (accounts: Account[]) => {
        if (account && accounts.find((a) => a.id === account.id)) return accounts.find((a) => a.id === account.id)
        if (accID && accounts.find((a) => a.id === accID)) return accounts.find((a) => a.id === accID)
        return accounts[0]
    }

    // retrieves a user
    const getUser = async (reloadProduct = false) => {
        console.info(`Fetching user contact ...`)
        setState({ fetching: true, error: null })
        const u = await getUserContact()
        if (!u) return setState({ fetching: false, error: 'Error retrieving user' })
        const ps = [...(findAccount(u.accounts)?.holdings ?? []), ...(findAccount(u.accounts)?.idCards ?? [])].sort(
            (a, b) => a.layoutPriority - b.layoutPriority
        )
        if (!recordedLogin) {
            recordUserLogin()
            setRecordedLogin(true)
        }
        return setState({
            user: u,
            account: findAccount(u.accounts),
            tsAndCs: u.tsAndCs,
            currentProduct: currentProduct && !reloadProduct ? ps.find((e) => e.id === currentProduct.id) ?? ps[0] : ps[0],
            products: ps,
            fetching: false,
        })
    }

    // logout a user
    const logout = async () => {
        auth.logout()
        theme.setKeys(['buttonBG', 'primaryOBJ', 'secondaryOBJ', 'accent', 'textLink', 'highlight'])
        theme.setKeys(['buttonText'])
        setState({ ...init, fetching: false })
    }

    // auth state handler function
    const handleAuthStateUpdate = (u: AuthIdentity | null) => {
        setState({ identity: u })
        if (u) {
            if (u.authenticationMethods[0].method === 'passkey') setUsedPasskey(true)
            getUser().then(notifyIOSWebkitOfLogin)
        } else {
            setState({ ...init, fetching: false })
        }
    }
    useEffect(() => {
        auth.setOnChange((u) => handleAuthStateUpdate(u))
        auth.getUser()
    }, [])

    return {
        identity,
        user,
        account,
        setCurrentAccount,
        products,
        currentProduct,
        setCurrentProduct,
        tsAndCs,
        getUser,
        fetching,
        logout,
        error,
    }
}

type AuthContext = ReturnType<typeof useAuthProvider>
const context = createContext<AuthContext>(init as AuthContext)
const { Provider } = context

export function AuthProvider({ children }: { children: ReactNode }) {
    return <Provider value={useAuthProvider()}>{children}</Provider>
}

export const useAuth = () => useContext(context)

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired,
}
